// src/services/alertService.js
// 自定義提示服務，使用 SweetAlert2 樣式但無需外部依賴

export const showAlert = (options) => {
  return new Promise((resolve) => {
    const {
      title = '',
      text = '',
      icon = 'info', // 'success', 'error', 'warning', 'info', 'question'
      showCancelButton = false,
      confirmButtonText = '確定',
      cancelButtonText = '取消',
      confirmButtonColor = '#3b82f6',
      cancelButtonColor = '#6b7280',
      dangerMode = false
    } = options;

    // 創建遮罩層
    const overlay = document.createElement('div');
    overlay.className = 'alert-overlay';
    
    // 創建提示框
    const alertBox = document.createElement('div');
    alertBox.className = `alert-box ${dangerMode ? 'danger' : ''}`;
    
    // 圖標映射
    const iconMap = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️',
      question: '❓'
    };

    alertBox.innerHTML = `
      <div class="alert-icon ${icon}">
        ${iconMap[icon] || iconMap.info}
      </div>
      ${title ? `<h3 class="alert-title">${title}</h3>` : ''}
      ${text ? `<p class="alert-text">${text}</p>` : ''}
      <div class="alert-buttons">
        ${showCancelButton ? `
          <button class="alert-btn cancel-btn" style="background-color: ${cancelButtonColor}">
            ${cancelButtonText}
          </button>
        ` : ''}
        <button class="alert-btn confirm-btn" style="background-color: ${confirmButtonColor}">
          ${confirmButtonText}
        </button>
      </div>
    `;

    overlay.appendChild(alertBox);
    document.body.appendChild(overlay);

    // 標記是否已經關閉，防止重複操作
    let isClosed = false;

    // 清理函數
    const cleanup = () => {
      if (isClosed) return;
      isClosed = true;

      // 移除事件監聽器
      document.removeEventListener('keydown', handleEsc);
      
      // 移除 DOM 元素
      if (overlay && document.body.contains(overlay)) {
        try {
          document.body.removeChild(overlay);
        } catch (error) {
          console.warn('Failed to remove overlay:', error);
        }
      }
    };

    // 事件處理
    const handleClose = (result) => {
      if (isClosed) return;
      
      overlay.classList.remove('show');
      alertBox.classList.remove('show');
      
      setTimeout(() => {
        cleanup();
        resolve(result);
      }, 300);
    };

    // ESC 鍵關閉
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        handleClose(false);
      }
    };

    // 添加動畫
    requestAnimationFrame(() => {
      if (!isClosed) {
        overlay.classList.add('show');
        alertBox.classList.add('show');
      }
    });

    // 綁定按鈕事件
    const confirmBtn = alertBox.querySelector('.confirm-btn');
    const cancelBtn = alertBox.querySelector('.cancel-btn');

    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => handleClose(true));
    }
    
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => handleClose(false));
    }

    // 點擊遮罩關閉
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        handleClose(false);
      }
    });

    // 添加鍵盤事件監聽器
    document.addEventListener('keydown', handleEsc);

    // 自動清理機制：30秒後自動清理（防止記憶體洩漏）
    const autoCleanupTimer = setTimeout(() => {
      if (!isClosed) {
        console.warn('Alert auto cleanup triggered');
        handleClose(false);
      }
    }, 30000);

    // 在關閉時清除定時器
    const originalResolve = resolve;
    resolve = (result) => {
      clearTimeout(autoCleanupTimer);
      originalResolve(result);
    };
  });
};

// 便捷方法
export const showSuccess = (title, text) => {
  return showAlert({
    title,
    text,
    icon: 'success',
    confirmButtonColor: '#10b981'
  });
};

export const showError = (title, text) => {
  return showAlert({
    title,
    text,
    icon: 'error',
    confirmButtonColor: '#ef4444'
  });
};

export const showWarning = (title, text) => {
  return showAlert({
    title,
    text,
    icon: 'warning',
    confirmButtonColor: '#f59e0b'
  });
};

export const showConfirm = (title, text, options = {}) => {
  return showAlert({
    title,
    text,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText || '確定',
    cancelButtonText: options.cancelButtonText || '取消',
    confirmButtonColor: options.confirmButtonColor || '#3b82f6',
    dangerMode: options.dangerMode || false
  });
};

export const showDeleteConfirm = (title = '確定要刪除嗎？', text = '此操作無法復原') => {
  return showAlert({
    title,
    text,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: '刪除',
    cancelButtonText: '取消',
    confirmButtonColor: '#ef4444',
    dangerMode: true
  });
};